import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { isOnboardingProgressBarVisible } from "../../utils/onboardingProgressBarUtils";
import {
  dismissPinAppBanner,
  isPinAppBannerSessionActive,
  markPinAppBannerPinned,
  readPinAppBannerState,
  recordPinAppBannerAppearance,
  resolvePinAppBanner,
} from "../../utils/pinAppBannerStorage";
import pinIcon from "./assets/pin-app-icon.png";
import pointingIllustration from "./assets/pin-app-pointing.png";
import classes from "./PinAppBanner.module.css";

/*
 * Quiet inline tip. No toast, modal, or focus move.
 * "Already pinned" is a local choice — Shopify does not report pin status.
 */
const PinAppBanner = ({ layout = "beside", onClose, onAlreadyPinned }) => {
  const card = (
    <div className={classes.card}>
      <button
        type="button"
        className={classes.close}
        aria-label="Close"
        onClick={onClose}
      >
        <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
          <path
            fill="currentColor"
            d="M11.414 10l4.293-4.293a1 1 0 0 0-1.414-1.414L10 8.586 5.707 4.293a1 1 0 1 0-1.414 1.414L8.586 10l-4.293 4.293a1 1 0 1 0 1.414 1.414L10 11.414l4.293 4.293a1 1 0 0 0 1.414-1.414L11.414 10z"
          />
        </svg>
      </button>
      <img className={classes.pinIcon} src={pinIcon} alt="" />
      <div className={classes.copy}>
        <p className={classes.tip}>
          <strong>Tip:</strong> pin IFH to your Shopify sidebar for faster access.
        </p>
        <button
          type="button"
          className={classes.alreadyPinned}
          onClick={onAlreadyPinned}
        >
          Already pinned
        </button>
      </div>
      <img
        className={classes.illustration}
        src={pointingIllustration}
        alt=""
      />
    </div>
  );

  if (layout === "compact") {
    return <div className={classes.compactRow}>{card}</div>;
  }

  return card;
};

/*
 * Decides beside vs compact vs hidden for this shop.
 * Job status must be loaded first so the compact card does not flash in
 * the gap before the progress strip appears (onboardingSyncJobStatus
 * starts as null).
 *
 * Location-switch and Incoming Inventory use the same slot. The tip sits
 * on the right of those bars too, under the same show/hide rules.
 *
 * Example: location switch running, first visit → placement "beside".
 * Example: onboarding finished, 7 days after the last show → "compact".
 */
export const usePinAppBanner = () => {
  const userDetails = useSelector((state) => state.user.userDetails);
  const currentActiveRoute = useSelector((state) => state.ui.currentActiveRoute);
  const {
    onboardingSyncJobStatus,
    historicalJobStatusObject,
    isOnboardingCompleted,
    ongoingJobStatus,
    locationSwitchInProgress,
    sync7DaysStatusRaw,
    sync60DaysStatusRaw,
  } = useSelector((state) => state.jobstatus);

  const [storageVersion, setStorageVersion] = useState(0);
  const shopName = userDetails?.shop_name || "";

  const jobStatusLoaded =
    (sync7DaysStatusRaw !== null && sync7DaysStatusRaw !== undefined) ||
    isOnboardingCompleted === true;

  const incomingInventoryBarVisible =
    currentActiveRoute === "incomingInventoryPage" && ongoingJobStatus === true;
  const otherBarVisible =
    locationSwitchInProgress === true || incomingInventoryBarVisible;

  /*
   * Any bar in this slot counts: onboarding, historical, location-switch,
   * or Incoming Inventory. The card goes on the right of whichever one
   * is actually showing.
   */
  const progressBarVisible =
    otherBarVisible ||
    isOnboardingProgressBarVisible({
      userDetails,
      onboardingSyncJobStatus,
      historicalJobStatusObject,
      isOnboardingCompleted,
      sync7DaysStatusRaw,
      sync60DaysStatusRaw,
      currentActiveRoute,
    });

  const decision = useMemo(
    () =>
      resolvePinAppBanner({
        shopName,
        progressBarVisible,
        jobStatusLoaded,
        state: readPinAppBannerState(shopName),
        sessionActive: isPinAppBannerSessionActive(shopName),
      }),
    [shopName, progressBarVisible, jobStatusLoaded, storageVersion]
  );

  const placement = decision.visible ? decision.placement : null;
  const record = decision.record;

  useEffect(() => {
    if (!shopName || !record) return;
    const wrote = recordPinAppBannerAppearance(shopName, record);
    if (wrote) {
      setStorageVersion((version) => version + 1);
    }
  }, [shopName, record]);

  const dismiss = () => {
    dismissPinAppBanner(shopName);
    setStorageVersion((version) => version + 1);
  };

  const markPinned = () => {
    markPinAppBannerPinned(shopName);
    setStorageVersion((version) => version + 1);
  };

  return { placement, dismiss, markPinned };
};

export default PinAppBanner;
